import { create } from 'zustand';
import type { Channel, ChatMessage, Panel, Presence, User } from '@bccomms/shared';

export type ConnStatus = 'idle' | 'connecting' | 'online' | 'reconnecting' | 'offline';
export type MediaStatus = 'idle' | 'starting' | 'ready' | 'nomic' | 'error';
export type Drawer = 'none' | 'chat' | 'people' | 'settings' | 'program' | 'mixer';
export type View = 'panel' | 'admin';
export type Layout = 'dial' | 'grid';
export type BtPttState = 'off' | 'scanning' | 'connecting' | 'connected' | 'unavailable' | 'unauthorized';

export interface Settings {
  micDeviceId: string | null;
  outputDeviceId: string | null;
  programDeviceId: string | null;
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
  sidetone: number;
  masterVolume: number;
  clicks: boolean;
  chimes: boolean;
  keepAwake: boolean;
  hotkeys: boolean;
  /** Main view: Unity-style dial or a grid of key tiles. */
  layout: Layout;
  /** Voice-activated talk: keys flagged `vox` on the panel talk automatically above this level. */
  voxEnabled: boolean;
  /** dBFS threshold the mic must exceed to trigger VOX. */
  voxThreshold: number;
  /** How long after dropping below threshold before VOX releases (debounce against gaps in speech). */
  voxHangMs: number;
  /** Native iOS: route audio to the loudspeaker instead of the earpiece. */
  speaker: boolean;
  /** Native apps: use a Bluetooth LE PTT button (PrymeBLU PTT-Z and compatibles) as the centre TALK key. */
  btPtt: boolean;
  /** The paired button's id, remembered after the first connection. */
  btPttDevice: string | null;
  /** Desktop app: use one channel of an audio interface (e.g. Dante Virtual Soundcard) as the mic. */
  micInterface: { device: string; channel: number } | null;
  /** Desktop app: route keys to individual output channels of the output device. */
  multichannelOut: boolean;
  /** Key id → output ('main' | 'm:N' | 's:N'); keys not listed play on the main mix (1–2). */
  outputMap: Record<string, string>;
  keyVolumes: Record<string, number>;
}

export const DEFAULT_SETTINGS: Settings = {
  micDeviceId: null,
  outputDeviceId: null,
  programDeviceId: null,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  sidetone: 0,
  masterVolume: 1,
  clicks: true,
  chimes: true,
  keepAwake: true,
  // Keyboard hotkeys only make sense with a physical keyboard + precise pointer (desktop).
  hotkeys: typeof window !== 'undefined' && window.matchMedia?.('(hover: hover) and (pointer: fine)').matches === true,
  layout: 'dial',
  voxEnabled: false,
  voxThreshold: -40,
  voxHangMs: 600,
  speaker: true,
  btPtt: false,
  btPttDevice: null,
  micInterface: null,
  multichannelOut: false,
  outputMap: {},
  keyVolumes: {},
};

export interface ConsumerInfo {
  peerId: string;
  role: 'mic' | 'program';
  channelId?: string;
}

export interface AppState {
  conn: ConnStatus;
  connError: string | null;
  media: MediaStatus;
  mediaError: string | null;
  kicked: string | null;

  serverName: string;
  user: User | null;
  panel: Panel | null;
  channels: Channel[];
  users: User[];
  presence: Record<string, Presence>;

  /** Optimistic local talk/listen state (server presence for self overrides). */
  talking: string[];
  listening: string[];
  /** Key ids currently latched on (grid view). */
  latched: string[];
  /** Key ids armed for the dial's centre TALK button. */
  armed: string[];
  /** Dial: centre button latched on ("continuous talk mode"). */
  continuous: boolean;
  /** Dial: centre button currently held. */
  centerHeld: boolean;
  /** Bluetooth PTT button link, as reported by the native plugin. */
  btPttState: BtPttState;
  btPttName: string | null;
  /** Key ids currently talking because VOX triggered them. */
  voxKeys: string[];
  /** Channel ids this client is feeding program audio into. */
  feeding: string[];

  consumers: Record<string, ConsumerInfo>;
  routes: Record<string, string[]>;
  /** target → expiry timestamp of an incoming CALL flash. */
  calls: Record<string, number>;
  micLevelDb: number;
  rttMs: number | null;

  chat: ChatMessage[];
  unread: Record<string, number>;

  view: View;
  drawer: Drawer;
  chatTarget: string | null;
  settings: Settings;
}

const SETTINGS_KEY = 'bccomms.settings.v1';

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<Settings>) };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function persistSettings(s: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {
    /* private mode etc. */
  }
}

const ARMED_KEY = 'bccomms.armed.v1';
export function loadArmed(): string[] {
  try {
    const raw = localStorage.getItem(ARMED_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}
export function persistArmed(ids: string[]): void {
  try {
    localStorage.setItem(ARMED_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

export const useStore = create<AppState>(() => ({
  conn: 'idle',
  connError: null,
  media: 'idle',
  mediaError: null,
  kicked: null,
  serverName: 'BCComms',
  user: null,
  panel: null,
  channels: [],
  users: [],
  presence: {},
  talking: [],
  listening: [],
  latched: [],
  armed: loadArmed(),
  continuous: false,
  centerHeld: false,
  btPttState: 'off',
  btPttName: null,
  voxKeys: [],
  feeding: [],
  consumers: {},
  routes: {},
  calls: {},
  micLevelDb: -100,
  rttMs: null,
  chat: [],
  unread: {},
  view: 'panel',
  drawer: 'none',
  chatTarget: null,
  settings: loadSettings(),
}));

export const setState = useStore.setState;
export const getState = useStore.getState;

export function updateSettings(patch: Partial<Settings>): Settings {
  const next = { ...getState().settings, ...patch };
  setState({ settings: next });
  persistSettings(next);
  return next;
}

// ---- selectors / helpers ---------------------------------------------------------------------

export function channelById(id: string): Channel | undefined {
  return getState().channels.find((c) => c.id === id);
}

export function userById(id: string): User | undefined {
  return getState().users.find((u) => u.id === id);
}

export function targetLabel(target: string, short = false): string {
  const [kind, id] = target.split(':');
  if (kind === 'ch') {
    const c = channelById(id);
    return c ? (short ? c.short : c.name) : '?';
  }
  const u = userById(id);
  return u ? (short ? u.displayName.slice(0, 6).toUpperCase() : u.displayName) : '?';
}

export function keyVolume(keyId: string): number {
  return getState().settings.keyVolumes[keyId] ?? 0.8;
}
